import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CameraIcon, CheckIcon, ImageIcon, MapPinIcon, PhoneIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { intents } from '../../data/demandes';
import type { AtnaTurnKind, Intent } from '../../types/demandes';
import { LocationPicker } from './LocationPicker';

/** Address “detected” by the simulated geolocation. */
const DETECTED_ADDRESS = '50 Avenue (Main Street), near 48 Street';
interface AtnaMessageProps {
  kind: AtnaTurnKind;
  /** Only the last assistant turn keeps its actions; history is inert. */
  live: boolean;
  intent: Intent;
  address: string;
  description: string;
  answer: string | null;
  hasPhoto: boolean;
  onConfirmIntent: () => void;
  onPickIntent: (intent: Intent) => void;
  onNotEmergency: () => void;
  onPickAddress: (address: string, source: 'position' | 'profil') => void;
  onAddPhoto: () => void;
  onSkipPhoto: () => void;
  onAnswer: (answer: string) => void;
  onSend: () => void;
  onEdit: () => void;
  onReset: () => void;
}
const primaryButton = 'rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const quietButton = 'text-sm font-semibold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
export function AtnaMessage({
  kind,
  live,
  intent,
  address,
  description,
  answer,
  hasPhoto,
  onConfirmIntent,
  onPickIntent,
  onNotEmergency,
  onPickAddress,
  onAddPhoto,
  onSkipPhoto,
  onAnswer,
  onSend,
  onEdit,
  onReset
}: AtnaMessageProps) {
  if (kind === 'urgence') {
    return <div className="border-l-4 border-red-600 pl-4" data-id="element-689" data-mp="1sgo9oahnj333">
        <p className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-id="element-690" data-mp="1xfhlzzl7co1o">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-691" data-mp="10qjsekp6uw63" />
          Possible emergency
        </p>
        <p className="mt-3 text-lg font-extrabold leading-snug text-ville-navy" data-id="element-692" data-mp="14o7t64dkbj5w">
          This may need immediate attention.
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-693" data-mp="1egnbj2sru9rb">
          Do not wait for an online request to be processed. Call 911 if someone is in danger, or the Town’s after-hours line for a dangerous hazard on public property.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-694" data-mp="lxgdym6w8yjl">
            <a href="tel:911" className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-695" data-mp="efryqhgsigrv">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-696" data-mp="gsgbrjvey45" />
              Call 911
            </a>
            <a href="tel:7808263496" className="inline-flex items-center gap-2 rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white" data-id="element-697" data-mp="3rnbrx8xj6s3i">
              Town after-hours line
            </a>
            <button type="button" onClick={onNotEmergency} className={quietButton} data-id="element-698" data-mp="3czicbo8g3pkx">
              This is not an emergency, continue
            </button>
          </div>}
      </div>;
  }
  if (kind === 'comprehension') {
    return <div data-id="element-699" data-mp="17u31jz86h08t">
        <p className={eyebrow} data-id="element-700" data-mp="17tku06g2ezll">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-701" data-mp="17ve09kmrnht2" />
          Here is what I understood:
        </p>
        <p className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-702" data-mp="1buwmcjqbpyxh">{intent.label}</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-703" data-mp="y26xm7jeufwo">{intent.service}</p>
        <p className="mt-3 text-[15px] text-slate-600" data-id="element-704" data-mp="13231m6s3y7c8">Is this the problem you want to report?</p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-705" data-mp="o4sjiekym37j">
            <button type="button" onClick={onConfirmIntent} className={primaryButton} data-id="element-706" data-mp="t69fpwsi11hs">
              Yes
            </button>
            {intents.filter(option => option.label !== intent.label).slice(0, 3).map(option => <button key={option.label} type="button" onClick={() => onPickIntent(option)} className={chipButton} data-id="element-707" data-mp="29iwzxfkpqnl0">
                  {option.label}
                </button>)}
          </div>}
      </div>;
  }
  if (kind === 'lieu') {
    return <div data-id="element-708" data-mp="2ok3vgv634y9p">
        <p className={eyebrow} data-id="element-709" data-mp="2jnn3fpvrlzrc">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-710" data-mp="2jog2e4ohfkyx" />
          Where is the problem?
        </p>
        {live ? <>
            <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-711" data-mp="2xgw6z9x17r1y">Pick the spot in one tap.</p>
            <LocationPicker onPick={onPickAddress} detectedAddress={DETECTED_ADDRESS} data-id="element-712" data-mp="2slyv7v8db3ld" />
          </> : <p className="mt-2 text-lg font-extrabold text-ville-navy" data-id="element-713" data-mp="32dv1htkly00o">{address}</p>}
      </div>;
  }
  if (kind === 'photo') {
    return <div data-id="element-714" data-mp="3gccp7raijcil">
        <p className={eyebrow} data-id="element-715" data-mp="3gcus36rokozh">
          <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-716" data-mp="3cc2gi6vc6801" />
          Photo
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-717" data-mp="3ca7wcurw2jx0">
          A photo would help the crew handle your request.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-718" data-mp="1hzclflt8esr2">
            <button type="button" onClick={onAddPhoto} className={`inline-flex items-center gap-2 ${primaryButton}`} data-id="element-719" data-mp="3h3b00jcz0vyu">
              <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-720" data-mp="3m2xd8sssuc7n" />
              Add a photo
            </button>
            <button type="button" onClick={onSkipPhoto} className={quietButton} data-id="element-721" data-mp="3r05vsuhv0i9f">
              Continue without a photo
            </button>
          </div>}
      </div>;
  }
  if (kind === 'detail') {
    return <div data-id="element-722" data-mp="3u6mev3mqi32">
        <p className={eyebrow} data-id="element-723" data-mp="8vchgzkrihfr">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-724" data-mp="8ukj5gzs65ls" />
          One last detail
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-725" data-mp="dlsuwjol2324">{intent.question}</p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-726" data-mp="ik454visifkv">
            {intent.answers.map(option => <li key={option} data-id="element-727" data-mp="nla255jhgp07">
                <button type="button" onClick={() => onAnswer(option)} className={chipButton} data-id="element-728" data-mp="ni5ntfcb0cvt">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'revue') {
    return <div data-id="element-729" data-mp="2817zub00hmk3">
        <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-730" data-mp="280pwyviuga37">Your request is ready</p>
        <h3 className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-731" data-mp="233gbfqyksikn">{intent.label}</h3>

        <dl className="mt-4 space-y-3 text-sm" data-id="element-732" data-mp="1y67tnrhcagjx">
          <div className="flex gap-3" data-id="element-733" data-mp="2vs54d2ph9hlb">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-734" data-mp="2vr2gl3ngbv9m">Location</dt>
            <dd className="flex items-center gap-1.5 font-semibold text-ville-navy" data-id="element-735" data-mp="2qttwuymnnj69">
              <MapPinIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-736" data-mp="2m1afdqkuk3w6" />
              {address}
            </dd>
          </div>
          <div className="flex gap-3" data-id="element-737" data-mp="2h2pgbuhbzq9b">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-738" data-mp="3ei3c1hpd58of">Description</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-739" data-mp="3ekfya725m57a">{description}</dd>
          </div>
          {answer && <div className="flex gap-3" data-id="element-740" data-mp="2i421kkebmo9w">
              <dt className="w-24 shrink-0 text-slate-500" data-id="element-741" data-mp="2i30m83q9vfmd">Detail</dt>
              <dd className="font-semibold text-ville-navy" data-id="element-742" data-mp="2mvbohd8hjdi3">{answer}</dd>
            </div>}
          <div className="flex gap-3" data-id="element-743" data-mp="293x4b32ud69g">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-744" data-mp="294ygjavlfysr">Photo</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-745" data-mp="2d47aafqmvwob">{hasPhoto ? '1 photo attached' : 'No photo'}</dd>
          </div>
          <div className="flex gap-3" data-id="element-746" data-mp="30vusm99txzez">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-747" data-mp="30sqjcxhicdhg">Contact details</dt>
            <dd className="text-slate-600" data-id="element-748" data-mp="35sv08ombva87">
              Bryon Mickelson ·{' '}
              <Link to="/my-profile" className="font-bold text-ville-indigo hover:underline" data-id="element-749" data-mp="2rt3g0657wxlr">
                Change this under My Profile
              </Link>
            </dd>
          </div>
        </dl>

        {live && <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-750" data-mp="1ggsumkst03rc">
            <button type="button" onClick={onSend} className={primaryButton} data-id="element-751" data-mp="1lekmjetg7pvj">
              Send my request
            </button>
            <button type="button" onClick={onEdit} className={quietButton} data-id="element-752" data-mp="1cyved72dr8f8">
              Change
            </button>
          </div>}
      </div>;
  }
  return <div data-id="element-753" data-mp="1mtndwgfiw5cn">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-754" data-mp="1mugc2t0f1mj6">
        <CheckIcon className="h-6 w-6" data-id="element-755" data-mp="u5rh7bpyedfv" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-756" data-mp="u6kfdoaujume">Request sent</h3>
      <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-757" data-mp="p54x2402027p">#REQ-2842</p>
      <p className="mt-3 text-[15px] text-slate-600" data-id="element-758" data-mp="13xot6kosd9fn">
        The responsible department has received your request. We will let you know when its status changes.
      </p>
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-759" data-mp="bei5cvspncl4">
          <Link to="/my-requests/req-2841" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-760" data-mp="6h89axatg2ob">
            Track my request
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-761" data-mp="2hd2xpkc62mls" />
          </Link>
          <button type="button" onClick={onReset} className={chipButton} data-id="element-762" data-mp="2hf5viqjk9k12">
            Report something else
          </button>
        </div>}
    </div>;
}